import {PopQuizCompletedQuizzesQuery} from 'src/generated/graphql';
import {PopQuizSetting, POP_QUIZ_SETTINGS} from 'src/lib/pop-quiz/pop-quiz-settings';

import {MS_PER_DAY, STALE_COURSE_DAYS} from '../constants';
import {CandidateSource, QuizCandidate} from '../types';
import {REFRESHER_COOLDOWN_DAYS, REQUIZ_COOLDOWN_DAYS} from './constants';

/**
 * Latest-attempt info for a single quiz, pulled in by the attempt-lookup query.
 */
export interface CandidateAttemptInfo {
    hasMetPassingScore: boolean;
    completionTime: string;
}

export interface CandidateStrategyContext {
    enrollmentsData: PopQuizCompletedQuizzesQuery | undefined;
    getPopQuizSetting: (setting: PopQuizSetting) => string;
    /** Bypasses today's-access / staleness / de-dupe rules (the `?showPopQuiz` QA override). */
    force: boolean;
    attemptsById: Record<string, CandidateAttemptInfo> | undefined;
}

/**
 * Inspects the shared context and returns zero or more candidates worth offering, each
 * tagged with its own `source`. Strategies never rank, exclude, or know about each other.
 */
export type CandidateStrategy = (context: CandidateStrategyContext) => QuizCandidate[];

/**
 *  Helper function to check if an enrollment is fresh enough to be considered for a pop quiz.
 */
function isEnrollmentFreshEnough(
    enrollment: {lastAccessedTime?: string | null},
    force: boolean,
    todayDate: string,
): boolean {
    if (force || !enrollment.lastAccessedTime) {
        return true;
    }
    if (new Date(enrollment.lastAccessedTime).toDateString() === todayDate) {
        return false;
    }
    const daysSinceAccess =
        (Date.now() - new Date(enrollment.lastAccessedTime).getTime()) / MS_PER_DAY;
    return daysSinceAccess <= STALE_COURSE_DAYS;
}

/**
 * Fallback strategy — returns every eligible candidate, in enrollment order
 */
export const buildDefaultCandidates: CandidateStrategy = ({
    enrollmentsData,
    getPopQuizSetting,
    force,
}) => {
    const enrollments = enrollmentsData?.me?.enrollments?.courseEnrollmentsPaged?.courseEnrollments;
    if (!enrollments) {
        return [];
    }

    const lastShownQuizId = force ? '' : getPopQuizSetting(POP_QUIZ_SETTINGS.lastQuizId);
    const todayDate = new Date().toDateString();
    const candidates: QuizCandidate[] = [];

    for (const enrollment of enrollments) {
        if (!enrollment || !isEnrollmentFreshEnough(enrollment, force, todayDate)) {
            continue;
        }

        const completedItems = enrollment.completedItems;
        if (!completedItems) {
            continue;
        }
        for (const item of completedItems) {
            if (item?.__typename === 'Quiz' && item.id && item.id !== lastShownQuizId) {
                const course = enrollment.course;
                if (!course?.urlCourseTaking) {
                    continue;
                }
                candidates.push({
                    quizId: Number(item.id),
                    quizTitle: item.title,
                    courseId: Number(course.id),
                    courseTitle: course.title,
                    courseUrl: String(course.urlCourseTaking),
                    courseProgress: Math.round(Number(enrollment.completionPercentage) || 0),
                    courseTopicId: course.primaryTopic?.id ?? null,
                    source: 'default',
                });
                break;
            }
        }
    }

    return candidates;
};

/**
 * Shared walk for the attempt-gated strategies (requiz, refresher)
 * Unlike `buildDefaultCandidates` (which breaks at the *first* completed quiz item in an
 * enrollment, unconditionally), this loop keeps scanning past a quiz that doesn't match —
 * intentional, since attempt-gated strategies care about which *specific* quiz in the
 * enrollment has qualifying attempt history, not just whichever one happens to be first.
 */
function buildAttemptGatedCandidates(
    {enrollmentsData, getPopQuizSetting, force, attemptsById}: CandidateStrategyContext,
    source: CandidateSource,
    cooldownDays: number,
    matchesAttempt: (attempt: CandidateAttemptInfo) => boolean,
): QuizCandidate[] {
    const enrollments = enrollmentsData?.me?.enrollments?.courseEnrollmentsPaged?.courseEnrollments;
    if (!enrollments || !attemptsById) {
        return [];
    }

    const lastShownQuizId = force ? '' : getPopQuizSetting(POP_QUIZ_SETTINGS.lastQuizId);
    const todayDate = new Date().toDateString();
    const candidates: QuizCandidate[] = [];

    for (const enrollment of enrollments) {
        if (!enrollment || !isEnrollmentFreshEnough(enrollment, force, todayDate)) {
            continue;
        }

        const completedItems = enrollment.completedItems;
        if (!completedItems) {
            continue;
        }

        for (const item of completedItems) {
            if (item?.__typename !== 'Quiz' || !item.id || item.id === lastShownQuizId) {
                continue;
            }

            const attempt = attemptsById[item.id];
            if (!attempt || !matchesAttempt(attempt)) {
                continue;
            }

            const daysSinceAttempt =
                (Date.now() - new Date(attempt.completionTime).getTime()) / MS_PER_DAY;
            if (!force && daysSinceAttempt < cooldownDays) {
                continue;
            }

            const course = enrollment.course;
            if (!course?.urlCourseTaking) {
                continue;
            }

            candidates.push({
                quizId: Number(item.id),
                quizTitle: item.title,
                courseId: Number(course.id),
                courseTitle: course.title,
                courseUrl: String(course.urlCourseTaking),
                courseProgress: Math.round(Number(enrollment.completionPercentage) || 0),
                courseTopicId: course.primaryTopic?.id ?? null,
                source,
                lastAttempt: attempt,
            });
            break;
        }
    }

    return candidates;
}

/**
 * Resurfaces a quiz the user previously failed, once REQUIZ_COOLDOWN_DAYS have passed since
 * that failed attempt — a lightweight spaced-repetition nudge
 */
export const buildRequizCandidates: CandidateStrategy = (context) =>
    buildAttemptGatedCandidates(
        context,
        'requiz',
        REQUIZ_COOLDOWN_DAYS,
        (attempt) => !attempt.hasMetPassingScore,
    );

/**
 * Resurfaces a quiz the user previously passed, once REFRESHER_COOLDOWN_DAYS have passed
 * since that attempt, as a refresher nudge back into a lapsed-but-unfinished course
 *
 * Note: this shares the same `isEnrollmentFreshEnough` / `STALE_COURSE_DAYS` (60-day) ceiling
 * as every other strategy, which in practice only leaves a 30-60-day-lapsed window for
 * refresher (`REFRESHER_COOLDOWN_DAYS` 30 up to `STALE_COURSE_DAYS` 60) — a course lapsed
 * longer than that won't get a refresher nudge. Intentional for now: a course untouched for
 * 60+ days may no longer be relevant to what the user's currently interested in, so leave the
 * shared ceiling as-is rather than giving refresher its own. Revisit once we have experiment
 * data on refresher engagement at different staleness windows.
 */
export const buildRefresherCandidates: CandidateStrategy = (context) =>
    buildAttemptGatedCandidates(
        context,
        'refresher',
        REFRESHER_COOLDOWN_DAYS,
        (attempt) => attempt.hasMetPassingScore,
    );
